<!--
TriggerBindingModal — camada Modal do slot (gatilho x tipo de canal).
Registrada em ModalManager, aberta por eventBus ('modal.triggerBinding.open').
-->
<template>
  <template v-if="isVisible && slot">
    <Modal
      :show="true"
      :title="modalTitle"
      size="medium"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="isSaveDisabled"
      :save-fn="handleSubmit"
      save-test-id="trigger-binding-submit"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <trigger-binding-form
        ref="formRef"
        :slot="slot"
        :triggers="triggers"
        @saved="handleSaved"
        @validation-failed="handleValidationFailed"
      />
    </Modal>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import Modal from '@/ui/Modal.vue'
import TriggerBindingForm from './TriggerBindingForm.vue'
import { eventBus } from '@/eventBus'
import { log } from '@/utils/DebugLogger'
import { FRANCHISOR_TRIGGER_LABELS } from './franchisorTriggerLabels'
import { useFranchisorChannels } from '@/composables/useFranchisorChannels'
import type { TriggerSlot } from './TriggerMatrixTable.vue'

interface FormInstance {
  canSave: boolean
  save: () => Promise<void>
}

const { t } = useI18n()
const { triggers } = useFranchisorChannels()

const isVisible = ref(false)
const slot = ref<TriggerSlot | null>(null)
const formRef = ref<FormInstance | null>(null)

const modalTitle = computed(() => {
  if (!slot.value) return t('Vinculação')
  const label = t(FRANCHISOR_TRIGGER_LABELS[slot.value.trigger_key] ?? slot.value.trigger_key)
  const channel = slot.value.channel_type === 'email' ? t('E-mail') : t('SMS')
  return `${label} — ${channel}`
})

const isSaveDisabled = (): boolean => !formRef.value?.canSave

function handleClose(): void {
  isVisible.value = false
  slot.value = null
}

async function handleSubmit(): Promise<void> {
  if (!formRef.value) return
  try {
    await formRef.value.save()
  } catch (err) {
    // Recusa nomeada ('channel_type_already_bound', 'trigger_not_implemented',
    // 'unknown_template_var:<x>') — o staff precisa ler o código, não uma
    // frase genérica. O modal fica ABERTO: o conserto é aqui dentro.
    const detail = err instanceof Error ? err.message : String(err)
    log('TriggerBindingModal', 'handleSubmit', 'Erro ao salvar vinculação', detail)
    eventBus.emit('globalMessage.error', t('Erro ao salvar vinculação') + ': ' + detail)
  }
}

function handleSaved(): void {
  eventBus.emit('triggerBinding.saved')
  handleClose()
}

function handleValidationFailed(): void {
  eventBus.emit('globalMessage.error', t('Escolha o canal e o template'))
}

function handleOpen(payload?: { slot?: TriggerSlot }): void {
  log('TriggerBindingModal', 'handleOpen', 'Abrindo modal de vinculação', payload)
  slot.value = payload?.slot ?? null
  isVisible.value = slot.value !== null
}

onMounted(() => {
  eventBus.on('modal.triggerBinding.open', handleOpen)
})

onBeforeUnmount(() => {
  eventBus.off('modal.triggerBinding.open', handleOpen)
})
</script>
